import { PIPELINE_STATUS_LABELS } from '@hoopoe/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { FileSpreadsheet, Lock } from 'lucide-react';
import { DataNumber, DataText } from '../components/data/data-text';
import { EmptyState } from '../components/guidance/empty-state';
import { PageHeader } from '../components/layout/app-shell';
import { useDatasets } from '../lib/datasets';

// Datasets (task 25): CSV, XLSX and JSON uploads, each opening the import wizard, with its latest import.

export const Route = createFileRoute('/datasets/')({ component: DatasetsPage });

const IMPORT_LABEL = { queued: 'Waiting to start', running: 'Importing', done: 'Imported', failed: 'Failed' } as const;

function DatasetsPage() {
  const datasets = useDatasets();
  const rows = datasets.data?.datasets ?? [];
  return (
    <div className="space-y-6">
      <PageHeader title="Datasets" description="Spreadsheets and data files (CSV, XLSX, JSON), imported column by column." />
      {datasets.isSuccess && rows.length === 0 && (
        <EmptyState icon={FileSpreadsheet} title="No datasets yet" learnMore="import-a-dataset">
          Upload a CSV, XLSX or JSON file, such as an actor list. Once it's processed, open it here to map its columns.
        </EmptyState>
      )}
      {rows.length > 0 && (
        <div className="relative overflow-x-auto rounded-lg border bg-surface">
          <table className="w-full text-sm">
            <caption className="sr-only">Datasets</caption>
            <thead>
              <tr className="border-b text-muted-foreground">
                <th scope="col" className="p-2 text-start font-medium">
                  File
                </th>
                <th scope="col" className="p-2 text-start font-medium">
                  Status
                </th>
                <th scope="col" className="p-2 text-start font-medium">
                  Last import
                </th>
                <th scope="col" className="p-2 text-start font-medium">
                  Uploaded
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b last:border-0">
                  <td className="p-2 font-medium">
                    <span className="inline-flex items-center gap-1">
                      <Link to="/datasets/$id" params={{ id: r.id }} className="underline-offset-2 hover:underline">
                        <DataText>{r.filename}</DataText>
                      </Link>
                      {r.sensitivity === 'confidential' && (
                        <span title="Confidential: processed on Cloudflare only" className="inline-flex">
                          <Lock className="size-3 text-muted-foreground" aria-hidden="true" />
                          <span className="sr-only">Confidential</span>
                        </span>
                      )}
                    </span>
                    <p className="text-xs font-normal text-muted-foreground">{r.format}</p>
                  </td>
                  <td className="p-2 text-xs">{PIPELINE_STATUS_LABELS[r.status]}</td>
                  <td className="p-2 text-xs">
                    {r.lastImport ? (
                      <>
                        {r.lastImport.stale ? 'Stopped' : IMPORT_LABEL[r.lastImport.status]}
                        {r.lastImport.status === 'done' && (
                          <span className="text-muted-foreground">
                            {' '}
                            · <DataNumber>{r.lastImport.stats.created}</DataNumber> created,{' '}
                            <DataNumber>{r.lastImport.stats.matched}</DataNumber> updated
                          </span>
                        )}
                      </>
                    ) : (
                      'Not imported yet'
                    )}
                  </td>
                  <td className="p-2 text-xs">
                    <DataNumber>
                      {new Date(r.createdAt).toLocaleString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </DataNumber>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
